import { axiosInstance } from "../axios-Instance";
import { toast } from "react-toastify";
import { toastOptions } from "../utils";
import { useContext } from "react";
import { AuthContext } from "../context";
import { getLoginToken, setLoginToken } from "../storage";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryKeys } from "../react-query/constants";

const SERVER_ERROR = "There was an error contacting the server.";

async function userLogin(formData) {
  const data = await axiosInstance({
    url: "/auth/login",
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "application/json",
    },
  });

  return data?.data;
}

async function getUsers() {
  const data = await axiosInstance({
    url: "/user",
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data?.data?.data;
}

const updateUser = async (formData) => {
  const data = await axiosInstance({
    url: `/user/${formData["_id"]}`,
    method: "PUT",
    data: formData,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });
  return data;
};

const deleteUser = async (formData) => {
  const data = await axiosInstance({
    url: `/user/${formData}`,
    method: "DELETE",

    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });
  return data;
};

async function userRegister(formData) {
  const data = await axiosInstance({
    url: "/user",
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "application/json",
    },
  });

  return data?.data;
}

async function forgotPassword(formData) {
  const data = await axiosInstance({
    url: "/auth/forgotPassword",
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "application/json",
    },
  });

  return data?.data;
}
const userReset = async (formData) => {
  const data = await axiosInstance({
    url: `/auth/resetpassword/${formData["token"]}`,
    method: "PUT",
    data: formData,
    headers: {
      "Content-Type": "application/json",
    },
  });
  return data;
};

export function useLogin() {
  const authCtx = useContext(AuthContext);
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => userLogin(formData),
    onSuccess: (data) => {
      setLoginToken(data.token);
      authCtx.authenticate(data.token);
    },
    onError: (error) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

export function useForgotPassword() {
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => forgotPassword(formData),
  });
  return { mutate, isError, error, isSuccess, reset };
}

export function useUpdateUser() {
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => updateUser(formData),
  });
  return { mutate, isError, error, isSuccess, reset };
}
export function useDeleteUser() {
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => deleteUser(formData),
  });
  return { mutate, isError, error, isSuccess, reset };
}

export function useResetPassword() {
  const { mutate, isSuccess, reset, error, isError } = useMutation({
    mutationFn: (formData) => userReset(formData),
  });
  return { mutate, isSuccess, reset, error, isError };
}

export function useRegister() {
  const authCtx = useContext(AuthContext);
  const { mutate, isError, error, isSuccess, reset } = useMutation({
    mutationFn: (formData) => userRegister(formData),
    onSuccess: (data) => {
      setLoginToken(data.token);
      authCtx.authenticate(data.token);
    },
    onError: (error) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });
  return { mutate, isError, error, isSuccess, reset };
}

export function useUsers() {
  const fallback = [];
  const { data = fallback } = useQuery({
    queryKey: [queryKeys.users],
    queryFn: getUsers,
  });
  return data;
}
